import React from 'react';
import styled from 'styled-components';
import { getKeyValuePairByKey } from 'utility/TechDataConverter';

import { IProduct, KeyValuePair } from 'models';

const emptyString = 'null';

interface IProps {
  selectedProduct: IProduct | null;
  techdata: any;
}

const TableTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const Table = styled.table`
  display: table;
  width: 100%;
  table-layout: auto;
`;

const Tr = styled.tr`
    width: 100%;
    :hover {
      background-color: #FF7203;
      color: white;
    }
`;

const Td = styled.td`
  width: 50%;
  color: inherit;
`;

const excludedKeys = ['feature'];

const rearrangedItems: { [key: string]: number; } = {
  'designation': 1,
  'application_types': 2,
  'feature_info': 3
};

const flattenObject = (obj: any): { [key: string]: string } => {
  const flattened: { [key: string]: string } = {};

  Object.keys(obj).forEach((key) => {
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      if (Array.isArray(obj[key])) {
        flattened[key] = obj[key];
      } else {
        if (!excludedKeys.includes(key)) {
          flattened[key] = emptyString;
        }
      }
      Object.assign(flattened, flattenObject(obj[key]));
    } else {
      flattened[key] = obj[key];
    }
  });

  return flattened;
};

const getSelectedEngineData = (selectedProduct: IProduct, techdata: any) => {
  let engineId = '';

  for (let i = 0; i < selectedProduct.metadata.length; i++) {
    if (selectedProduct.metadata[i].key === 'engine-id') {
      engineId = selectedProduct.metadata[i].value;
      break;
    }
  }

  const index = techdata.findIndex((obj: any) => obj != null && obj.engine_id === parseInt(engineId));
  if (index === -1) return null;

  return Object.entries(flattenObject(techdata[index]));
};

const rearrangeItems = (entries: any[]) => {
  for (const key in rearrangedItems) {
    const item = entries.find((x: any) => x[0] === key);
    if (item !== undefined) {
      const oldIndex = entries.indexOf(item);
      const newIndex = rearrangedItems[key];
      entries.splice(newIndex, 0, entries.splice(oldIndex, 1)[0]);
    }
  }
};

const buildTechDataTableRows = (selectedProduct: IProduct | null, techdata: any) => {
  if (!selectedProduct) return <div />;

  const entries = getSelectedEngineData(selectedProduct, techdata);

  if (!entries) return <div>Technical data cannot be found for this engine</div>;

  rearrangeItems(entries);

  const noteEntries: any[] = [];

  const generalData = entries.map((entry) => {
    const key = entry[0];
    const pair = getKeyValuePairByKey(key);

    if (pair && pair.type === 'note') {
      noteEntries.push(entry);
      return (null);
    }

    return buildTableEntry(entry, pair);
  });

  const notes = noteEntries.map((entry) => {
    const key = entry[0];
    const pair = getKeyValuePairByKey(key);
    return buildTableEntry(entry, pair);
  });

  return <div>{generalData}{notes}</div>;
};

function buildTableEntry(entry: [string, string], pair: KeyValuePair | undefined) {
  const key = entry[0];
  let value = entry[1];

  if (Array.isArray(value)) {
    value = getCommaSeparatedStringFromArray(value);
  }

  if (!pair || value === null || parseFloat(value) === 0) return (null);

  if (value !== emptyString) {
    return (
      <Tr key={key}>
        <Td>{pair.value}</Td>
        <Td>{value} {pair.unit}</Td>
      </Tr>
    );
  } else {
    return (
      <tr key={key}>
        <td>
          <br />
          <TableTitle>
            {pair.value}
          </TableTitle>
        </td>
      </tr>
    );
  }
}

function getCommaSeparatedStringFromArray(array: string & any[]): string {
  let valueHolder = '';

  array.forEach((val: string) => {
    let divider = ', ';
    if (array.indexOf(val) === 0) {
      divider = ' ';
    }

    valueHolder = `${valueHolder}${divider}${val}`;
  });

  return valueHolder;
}

const TechDataTable: React.FC<IProps> = ({ techdata, selectedProduct }) => {
  return (
    <div>
      <Table>
        <tbody>
          <tr key="title">
            <Td>
              <TableTitle>
                Engine Technical Information
                </TableTitle>
            </Td>
          </tr>
          {buildTechDataTableRows(selectedProduct, techdata)}
        </tbody>
      </Table>
    </div>
  );
};

export default TechDataTable;
